/**
 * Renders the image bundled with a BAMF question.
 *
 * Filename convention: `public/einbuergerungstest/<bamf_id>.svg` is preferred
 * (smaller, sharp at any scale), falling back to `.png`. When neither exists,
 * shows a dashed placeholder so layout doesn't jump.
 *
 * Image attribution lives in `public/einbuergerungstest/CREDITS.md`.
 */
import { ImageOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

type Size = 'sm' | 'md';

type Props = {
  bamfId: string;
  /** Alt text — defaults to a generic, accessibility-passing string. */
  alt?: string;
  size?: Size;
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-24',
  md: 'h-32',
};

export function QuestionImage({ bamfId, alt, size = 'md' }: Props) {
  // Track which extension we've tried. Order: svg → png → give up.
  const [stage, setStage] = useState<'svg' | 'png' | 'fail'>('svg');

  useEffect(() => {
    setStage('svg');
  }, [bamfId]);

  if (stage === 'fail') {
    return (
      <div
        className={cn(
          'border-border text-muted-foreground flex w-full items-center justify-center gap-2 rounded-lg border border-dashed text-xs',
          SIZE_CLASSES[size],
        )}
      >
        <ImageOff className="size-4" />
        <span>Image not bundled (Question {bamfId})</span>
      </div>
    );
  }

  const ext = stage; // 'svg' or 'png'
  return (
    <img
      src={`/einbuergerungstest/${bamfId}.${ext}`}
      alt={alt ?? `Illustration for question ${bamfId}`}
      onError={() => setStage(stage === 'svg' ? 'png' : 'fail')}
      className={cn(
        'border-border bg-card mx-auto w-auto max-w-full rounded-lg border object-contain p-2',
        SIZE_CLASSES[size],
      )}
    />
  );
}
